import { FunctionComponent, useEffect, useState } from 'react';
import { Stack, SvgIcon, Typography, useTheme } from '@mui/material';
import { Markdown } from '@/components/Layout/Markdown';
import { useGetProductConfigurationsQuery } from '@/api/v2/productConfigurations';
import { useAuthentication } from '@/hooks/useAuthentication';
import { StarjackTable } from '@/components/Table/v2/Table';
import { usePagination } from '@/hooks/usePagination';
import { ProductConfiguration } from '@/models/product';
import DocumentMagnifyingGlassIcon from '@heroicons/react/24/solid/DocumentMagnifyingGlassIcon';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import { ConfigurationsPanelContainer } from '@/pageContents/productConfiguration/ProductConfigurationBulkUpdate/ConfigurationsPanelContainer';
import { useConfigurationPanelContext } from '@/pageContents/productConfiguration/ProductConfigurationBulkUpdate/ConfigurationPanelContext';
import { useSlice } from '@/hooks/useSlice';
import { pageDataSlice } from '@starjack/features/pageData';
import { ProductConfigurationOverview } from './ProductConfigurationOverview';

export const ProductConfigurationContent: FunctionComponent = () => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data, isLoading } = useGetProductConfigurationsQuery({
        liftOperatorId,
    });
    const {
        actions: { setPageData, setExplanationVideo },
    } = useSlice(pageDataSlice, 'requestSlice');
    const theme = useTheme();
    const { isActive, isCollapsed } = useConfigurationPanelContext();
    const { currentItems, setItemsPerPage, setNextPage, itemsPerPage } =
        usePagination({
            items: data,
            itemsPerPage: 10,
        });
    const [selectedRows, setSelectedRows] = useState<ProductConfiguration[]>(
        []
    );

    const containerMarginBottom = isActive ? (!isCollapsed ? 88 : 6) : 0;

    useEffect(() => {
        setPageData({
            title: 'Produktkonfiguration',
            body: (
                <Markdown source='<p><b>Hier können Sie</b><br><ul><li>Die Anzeige der Ticketangebote im Webshop zeitlich eingrenzen</li><li>Die Buchungsarten für die einzelnen Buchungskanäle definieren</li><li>Promotion-Codes hinterlegen, um Angebote nur für bestimmte Nutzergruppen sichtbar zu machen</li></ul><br /><!-- react-text: 33 -->Ihre Tickets sind hier nach der im Buchungssystem (AXESS / SKIDATA) hinterlegten Ticketbeschreibung aufgelistet.<!-- /react-text --><br><!-- react-text: 35 -->Eine Konfiguration bezieht sich daher immer auf alle Tickets mit gleicher Ticketbeschreibung.<!-- /react-text --><br><br><!-- react-text: 38 -->Für Promotion-Tickets empfehlen wir, eine eindeutig abweichende Ticketbeschreibung in Ihrem Buchungssystem zu hinterlegen.<!-- /react-text --></p>' />
            ),
        });
        setExplanationVideo(
            'https://my-starjack.fleeq.io/l/vvs11f19di-t7m2ptksa0'
        );
    }, []);

    return (
        <>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Produktkonfiguration
            </Typography>
            <Stack
                sx={{
                    mb: containerMarginBottom,
                }}
                spacing={theme.page.verticalSpacing}
            >
                <StarjackTable<ProductConfiguration>
                    count={data?.length || 0}
                    allItems={data}
                    items={currentItems}
                    columns={[
                        {
                            label: 'Produkt',
                            itemTransform: (productConfiguration) =>
                                productConfiguration.product.name.de,
                        },
                        {
                            label: 'Konfiguration aktiviert',
                            itemTransform: (productConfiguration) => {
                                return productConfiguration.id ? (
                                    <ProductConfigurationOverview
                                        productConfiguration={
                                            productConfiguration
                                        }
                                    />
                                ) : (
                                    'Nein'
                                );
                            },
                        },

                        {
                            label: 'Aktion',
                            itemTransform: (productConfiguration) => (
                                <Link
                                    href={`/produktkonfiguration/details/?id=${productConfiguration.productId}`}
                                >
                                    <IconButton
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        <SvgIcon>
                                            <DocumentMagnifyingGlassIcon />
                                        </SvgIcon>
                                    </IconButton>
                                </Link>
                            ),
                        },
                    ]}
                    itemId={'id'}
                    onPageChange={setNextPage}
                    onRowsPerPageChange={setItemsPerPage}
                    rowsPerPage={itemsPerPage}
                    loading={isLoading}
                    onSelect={(_, allSelected) => {
                        setSelectedRows(allSelected);
                    }}
                    onDeselect={(_, nextSelected) => {
                        setSelectedRows(nextSelected);
                    }}
                    onSelectAll={(selected) => {
                        setSelectedRows(selected);
                    }}
                    onDeselectAll={() => setSelectedRows([])}
                    rowsSelectable
                />

                <ConfigurationsPanelContainer
                    selectedConfigurations={selectedRows}
                />
            </Stack>
        </>
    );
};
